import React, { useState, useEffect } from "react";
import { ArrowLeft, Search, Calendar, CreditCard, CheckCircle, XCircle, Clock, Copy, Download, CheckCircle2, AlertCircle } from "lucide-react";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import toast from "react-hot-toast";
import Layout from "../../layout/realestateagent/Layout";

const Transaction = ({ isContentOnly = false }) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");

    useEffect(() => {
        fetchTransactions();
    }, []);

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            const response = await axiosInstancev1.get("/properties/transactions");
            if (response.data.success) {
                setTransactions(response.data.transactions || []);
            }
        } catch (error) {
            console.error("Error fetching transactions:", error);
            toast.error("Failed to load transaction history");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return {
            date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
            time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
        };
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Transaction ID copied!");
    };

    const getStatusStyles = (status) => {
        switch (status?.toLowerCase()) {
            case 'success':
                return { bg: 'bg-green-50', text: 'text-green-700', icon: <CheckCircle2 className="w-4 h-4" /> };
            case 'failed':
                return { bg: 'bg-red-50', text: 'text-red-700', icon: <XCircle className="w-4 h-4" /> };
            case 'pending':
                return { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: <Clock className="w-4 h-4" /> };
            default:
                return { bg: 'bg-gray-50', text: 'text-gray-700', icon: <AlertCircle className="w-4 h-4" /> };
        }
    };

    const getListingStatusBadge = (status) => {
        const config = {
            active: { bg: "bg-green-100", text: "text-green-700", label: "Active" },
            expired: { bg: "bg-red-100", text: "text-red-700", label: "Expired" },
            pending_payment: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Pending Payment" },
            draft: { bg: "bg-gray-100", text: "text-gray-700", label: "Draft" }
        };
        const style = config[status] || config.draft;
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
                {style.label}
            </span>
        );
    };

    const filteredTransactions = transactions.filter(t => {
        const matchesSearch = t.merchant_order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            t.property_id?.toString().includes(searchTerm) ||
            t.property_category?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = typeFilter === "all" || t.property_category?.toLowerCase() === typeFilter.toLowerCase();
        const isSuccessful = t.payment_status?.toLowerCase() === 'success';
        return matchesSearch && matchesType && isSuccessful;
    });

    const content = (
        <div className={`min-h-screen p-4 lg:p-8 ${!isContentOnly ? 'bg-[#E0E0E0]' : ''}`}>
            {/* Header Section */}
            <div className="mb-8 bg-white p-6 rounded-[28px] shadow-sm">
                <h1 className="text-2xl font-bold text-gray-800">Transaction History</h1>
                <p className="text-gray-600 mt-1 text-sm">Manage and track your property posting payments</p>
            </div>

            {/* Filters & Content */}
            <div className="bg-white rounded-[28px] shadow-sm overflow-hidden">
                {/* Toolbar */}
                <div className="p-6 border-b border-gray-100">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by Transaction ID..."
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-blue-500 transition-all outline-none shadow-inner"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <select
                                className="bg-gray-50 border-none rounded-xl text-sm px-4 py-2.5 focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <option value="all">All Categories</option>
                                <option value="housing">House</option>
                                <option value="plot">Property</option>
                            </select>
                            <button className="p-2.5 hover:bg-gray-50 rounded-xl transition-colors text-gray-400">
                                <Download className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="p-20 flex flex-col items-center justify-center">
                        <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="mt-4 text-gray-500 text-sm">Fetching records...</p>
                    </div>
                ) : filteredTransactions.length === 0 ? (
                    <div className="p-20 text-center">
                        <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search className="w-8 h-8 text-gray-300" />
                        </div>
                        <h3 className="text-gray-800 font-semibold text-lg">No records found</h3>
                        <p className="text-gray-500 text-sm mt-1">Payments for your property posts will appear here.</p>
                    </div>
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50/50">
                                        <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time & Date</th>
                                        <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Transaction ID</th>
                                        <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Paid Amount</th>
                                        <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Status</th>
                                        <th className="py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Listing</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredTransactions.map((item) => {
                                        const { date, time } = formatDate(item.created_at);
                                        const status = getStatusStyles(item.payment_status);

                                        return (
                                            <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="py-4 px-6">
                                                    <p className="text-sm font-medium text-gray-800">{date}</p>
                                                    <p className="text-[11px] text-gray-400 mt-0.5">{time}</p>
                                                </td>
                                                <td className="py-4 px-6">
                                                    <div className="flex items-center gap-2 group">
                                                        <span className="text-sm font-mono text-gray-600 uppercase">
                                                            {item.merchant_order_id}
                                                        </span>
                                                        <button
                                                            onClick={() => copyToClipboard(item.merchant_order_id)}
                                                            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-all"
                                                        >
                                                            <Copy className="w-3 h-3 text-gray-400" />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-6">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize 
                                                                ${item.property_category === 'housing' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                                                        {item.property_category === 'housing' ? 'House' : 'Property'}
                                                    </span>
                                                </td>
                                                <td className="py-4 px-6 text-right">
                                                    <p className="text-sm font-bold text-gray-800">
                                                        ₹{parseFloat(item.amount).toLocaleString('en-IN')}
                                                    </p>
                                                </td>
                                                <td className="py-4 px-6">
                                                    <div className="flex justify-center">
                                                        <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full ${status.bg} ${status.text} text-[11px] font-bold uppercase`}>
                                                            {status.icon}
                                                            {item.payment_status}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-6 text-center">
                                                    {getListingStatusBadge(item.status)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="md:hidden space-y-4 p-4 bg-gray-50">
                            {filteredTransactions.map((item) => {
                                const { date, time } = formatDate(item.created_at);
                                const status = getStatusStyles(item.payment_status);

                                return (
                                    <div key={item.id} className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100/50 active:scale-[0.98] transition-all">
                                        <div className="space-y-5">
                                            {/* Time & Date */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-400 text-[11px] uppercase font-bold tracking-wider">Date & Time</span>
                                                <span className="text-gray-800 text-sm font-semibold">{date}, {time}</span>
                                            </div>

                                            {/* Transaction ID */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-400 text-[11px] uppercase font-bold tracking-wider">Order ID</span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-mono text-gray-500 bg-gray-50 px-2.5 py-1 rounded-lg border border-gray-100">
                                                        {item.merchant_order_id?.slice(0, 8)}...{item.merchant_order_id?.slice(-4)}
                                                    </span>
                                                    <button
                                                        onClick={() => copyToClipboard(item.merchant_order_id)}
                                                        className="text-gray-400 p-1 hover:text-blue-600 transition-colors"
                                                    >
                                                        <Copy className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Paid Amount */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-400 text-[11px] uppercase font-bold tracking-wider">Amount Paid</span>
                                                <span className="text-xl font-black text-gray-900">
                                                    ₹{parseFloat(item.amount).toLocaleString('en-IN')}
                                                </span>
                                            </div>

                                            {/* Type */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-400 text-[11px] uppercase font-bold tracking-wider">Category</span>
                                                <span className={`px-3 py-1 rounded-lg text-[10px] font-bold tracking-wider uppercase 
                                                            ${item.property_category === 'housing' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                                                    {item.property_category === 'housing' ? 'House' : 'Property'}
                                                </span>
                                            </div>

                                            {/* Status Footer */}
                                            <div className="flex justify-between items-center pt-4 border-t border-gray-50">
                                                <div className="flex items-center gap-2">
                                                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full ${status.bg} ${status.text} text-[10px] font-bold uppercase`}>
                                                        {status.icon}
                                                        {item.payment_status}
                                                    </div>
                                                </div>
                                                {getListingStatusBadge(item.status)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    if (isContentOnly) return content;

    return (
        <div style={{ backgroundColor: "#E0E0E0" }}>
            <Layout>
                {content}
            </Layout>
        </div>
    );
};

export default Transaction;
